import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { updateUserInfo } from "@/store/redux/slices/auth/auth_slice";
import { searchUserByFieldAsync } from "@/store/redux/slices/user/user_slice";
import Button from "@/components/common/Button";
import OAuthButtons from "@/components/shared/OAuth_Buttons/OAuth_Buttons";
import Input from "@/components/common/inputs/Input";
import { useLoader } from "@/hooks/LoaderProvider";
import { ArrowRightOutlined } from "@/config/antd";
import Error from "@/components/common/alerts/Error";
import WebIcon from "@/components/common/Web_Icons";
import Logo from "@/components/common/Logo";

const EmailSignIn = () => {
  const [email, setEmail] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  // Get user search states from Redux (currently unused but kept for future error handling)
  // const searchedUser = useSelector(selectSearchedUser);
  // const isSearching = useSelector(selectIsSearching);
  // const searchError = useSelector(selectSearchError);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setIsErrorVisible(false);
  };

  useEffect(() => {
    setIsActive(email.trim() !== "");
  }, [email]);

  const handleProceed = useCallback(async () => {
    showLoader();
    try {
      const resultAction = await dispatch(
        searchUserByFieldAsync({
          key: "email",
          value: email.toLowerCase(),
        })
      );

      if (searchUserByFieldAsync.fulfilled.match(resultAction)) {
        const userData = resultAction.payload?.data?.[0];

        // Successful HTTP response with no matching user
        if (!userData) {
          setIsErrorVisible(true);
          return;
        }

        const userInfoUpdate = {
          email: email.toLowerCase(),
          userID: userData?._id || "",
          fullname: userData?.fullname || "",
          phone: userData?.phone || "",
          profileImage:
            userData?.profileImage ||
            userData?.emsUserInfo?.profileImage ||
            "",
        };

        dispatch(updateUserInfo(userInfoUpdate));

        navigate("/signin/password", {
          state: { email: email.toLowerCase() },
        });
        return;
      }

      // CORS / offline / unreachable API — global Network modal already covers this.
      // Do not also show the "Account does not Exist" modal.
      const payload = resultAction.payload;
      const errorMessage =
        typeof payload === "string"
          ? payload
          : payload?.message || resultAction.error?.message || "";
      const isNetworkFailure =
        /network error/i.test(String(errorMessage)) ||
        resultAction.error?.code === "ERR_NETWORK" ||
        (resultAction.error?.name === "AxiosError" &&
          !resultAction.error?.response);

      if (!isNetworkFailure) {
        setIsErrorVisible(true);
      }
    } finally {
      hideLoader();
    }
  }, [email, navigate, showLoader, hideLoader, dispatch]);

  const handleCloseError = () => {
    setIsErrorVisible(false);
  };

  const handleForgotEmail = () => {
   
    if (email.trim()) {
      dispatch(updateUserInfo({ email: email.trim().toLowerCase() }));
    }
    showLoader();
    setTimeout(() => {
      navigate("/account-recovery/email-recovery");
      hideLoader();
    }, 2000);
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "-100%" }}
      transition={{ type: "tween", ease: "anticipate", duration: 0.5 }}
      className="flex flex-col h-full md:justify-center">
      <div className="flex flex-col flex-1 p-4 pt-2 md:pt-4 mt-[-15rem] md:mt-auto">
        <div className="flex flex-col flex-1">
          <div className="flex items-center mb-2">
            <Logo name="VeegilLogo" size="small" />
            <h1 className="ml-2 heading-large text-primary">Sign in</h1>
          </div>

          <div>
            <p className="mb-6 text-left text-darkGrey text-small">
              Welcome to our service. We&apos;re thrilled that you&apos;re
              interested in using our service.
            </p>
          </div>

          <div className="mb-4">
            <Input
              data-testid="email-input"
              label="EMAIL ADDRESS"
              placeholder="email@domain.com"
              icon={<WebIcon icon="Message" size="small" />}
              type="email"
              value={email}
              onChange={handleEmailChange}
              id="email"
            />
          </div>

          <div>
            <Button
              data-testid="proceed-button"
              text="Proceed"
              icon={<ArrowRightOutlined />}
              onClick={handleProceed}
              disabled={!email}
              isActive={isActive}
              color="primary"
              className="w-full"
            />
          </div>

          <div className="mt-4 text-left">
            <Link
              to="#"
              onClick={handleForgotEmail}
              className="font-medium underline text-primary text-small">
              Forgot your Email?
            </Link>
          </div>

          <div className="mt-6 text-left">
            <p className="mb-4 font-bold text-black text-small">
              Sign in using
            </p>

            {/* OAuth Buttons */}
            <OAuthButtons />
          </div>

          <div className="mt-6 text-left">
            <p className="text-small text-darkGrey whitespace-nowrap">
              Don&apos;t have a Veegil Account yet?{" "}
              <Link
                to="/signup"
                className="font-medium text-primary hover:underline">
                Sign up now
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header="Account does not Exist!"
        paragraph="The Phone Number or Email you entered does not exist in our database. Please check again to verify."
        buttonText="Recovery Center"
        linkText="Did you forget your Email or Phone Number?"
        linkPath="/signup"
        recoveryPath="/account-recovery/email-recovery"
      />
    </motion.div>
  );
};

export default EmailSignIn;
